'use client'

import React from 'react'
import { AlertTriangle, CheckCircle, Info, Trash2, X, Loader2 } from 'lucide-react'
import { BaseDialog, BaseDialogProps } from './Dialog'
import { cn } from '../utils/styles'

// Button component for dialog actions
const Button = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link' | 'success'
    size?: 'default' | 'sm' | 'lg' | 'icon'
  }
>(({ className, variant = 'default', size = 'default', ...props }, ref) => {
  const variants = {
    default: 'bg-primary text-primary-foreground hover:bg-primary/90',
    destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
    outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
    ghost: 'hover:bg-accent hover:text-accent-foreground',
    link: 'text-primary underline-offset-4 hover:underline',
    success: 'bg-green-600 text-white hover:bg-green-700',
  }

  const sizes = {
    default: 'h-10 px-4 py-2',
    sm: 'h-9 rounded-md px-3',
    lg: 'h-11 rounded-md px-8',
    icon: 'h-10 w-10',
  }

  return (
    <button
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
        variants[variant],
        sizes[size],
        className
      )}
      ref={ref}
      {...props}
    />
  )
})
Button.displayName = 'Button'

export interface ConfirmDialogProps extends Omit<BaseDialogProps, 'children' | 'footer'> {
  // Action handlers
  onConfirm: () => void | Promise<void>
  onCancel?: () => void

  // Button text
  confirmText?: string
  cancelText?: string

  // Styling and variants
  variant?: 'default' | 'destructive' | 'success' | 'warning' | 'info'
  confirmVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link' | 'success'

  // Loading states
  isLoading?: boolean
  loadingText?: string

  // Icon configuration
  icon?: React.ReactNode | 'auto' | false
  showIcon?: boolean

  // Layout options
  hideCancel?: boolean
  children?: React.ReactNode
}

// Icon mapping for different variants
const getVariantIcon = (variant: ConfirmDialogProps['variant']) => {
  const iconMap = {
    default: <Info className="h-6 w-6 text-blue-600" />,
    destructive: <AlertTriangle className="h-6 w-6 text-red-600" />,
    success: <CheckCircle className="h-6 w-6 text-green-600" />,
    warning: <AlertTriangle className="h-6 w-6 text-yellow-600" />,
    info: <Info className="h-6 w-6 text-blue-600" />,
  }
  return iconMap[variant || 'default']
}

export function ConfirmDialog({
  onConfirm,
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  confirmVariant,
  isLoading = false,
  loadingText = 'Loading...',
  icon = 'auto',
  showIcon = true,
  hideCancel = false,
  children,
  ...dialogProps
}: ConfirmDialogProps) {
  const [isProcessing, setIsProcessing] = React.useState(false)

  const actualConfirmVariant = confirmVariant || (variant === 'destructive' ? 'destructive' : 'default')
  const isActionLoading = isLoading || isProcessing

  const handleConfirm = async () => {
    if (isActionLoading) return

    setIsProcessing(true)
    try {
      await onConfirm()
      dialogProps.onOpenChange(false)
    } catch (error) {
      console.error('Confirmation action failed:', error)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleCancel = () => {
    if (isActionLoading) return
    onCancel?.()
    dialogProps.onOpenChange(false)
  }

  const displayIcon = showIcon && icon !== false ? (
    icon === 'auto' ? getVariantIcon(variant) : icon
  ) : null

  const footer = (
    <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 gap-2">
      {!hideCancel && (
        <Button
          variant="outline"
          onClick={handleCancel}
          disabled={isActionLoading}
        >
          {cancelText}
        </Button>
      )}
      <Button
        variant={actualConfirmVariant}
        onClick={handleConfirm}
        disabled={isActionLoading}
      >
        {isActionLoading ? (
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>{loadingText}</span>
          </div>
        ) : (
          confirmText
        )}
      </Button>
    </div>
  )

  return (
    <BaseDialog
      {...dialogProps}
      footer={footer}
      showCloseButton={!isActionLoading}
    >
      <div className="flex gap-4">
        {displayIcon && (
          <div className="flex-shrink-0">
            {displayIcon}
          </div>
        )}
        <div className="flex-1">
          {children}
        </div>
      </div>
    </BaseDialog>
  )
}

// Specialized confirmation dialogs
export interface DeleteConfirmDialogProps extends Omit<ConfirmDialogProps, 'variant' | 'confirmVariant' | 'icon' | 'title' | 'confirmText'> {
  itemName: string
  itemType?: string
  title?: string
  confirmText?: string
}

export function DeleteConfirmDialog({
  itemName,
  itemType = 'item',
  title,
  confirmText = 'Delete',
  description,
  ...props
}: DeleteConfirmDialogProps) {
  const defaultTitle = title || `Delete ${itemName}?`
  const defaultDescription = description ||
    `Are you sure you want to delete this ${itemType.toLowerCase()}? This action cannot be undone.`

  return (
    <ConfirmDialog
      {...props}
      title={defaultTitle}
      description={defaultDescription}
      confirmText={confirmText}
      variant="destructive"
      confirmVariant="destructive"
      icon={<Trash2 className="h-6 w-6 text-red-600" />}
    />
  )
}

export interface SaveConfirmDialogProps extends Omit<ConfirmDialogProps, 'variant' | 'confirmVariant' | 'icon' | 'confirmText'> {
  confirmText?: string
}

export function SaveConfirmDialog({
  confirmText = 'Save Changes',
  ...props
}: SaveConfirmDialogProps) {
  return (
    <ConfirmDialog
      {...props}
      confirmText={confirmText}
      variant="success"
      confirmVariant="success"
      icon={<CheckCircle className="h-6 w-6 text-green-600" />}
    />
  )
}

export interface DiscardConfirmDialogProps extends Omit<ConfirmDialogProps, 'variant' | 'confirmVariant' | 'icon' | 'confirmText' | 'title'> {
  confirmText?: string
  title?: string
}

export function DiscardConfirmDialog({
  confirmText = 'Discard Changes',
  title = 'Discard unsaved changes?',
  description = 'You have unsaved changes. Are you sure you want to discard them?',
  ...props
}: DiscardConfirmDialogProps) {
  return (
    <ConfirmDialog
      {...props}
      title={title}
      description={description}
      confirmText={confirmText}
      variant="warning"
      confirmVariant="destructive"
      icon={<AlertTriangle className="h-6 w-6 text-yellow-600" />}
    />
  )
}

export default ConfirmDialog